import React, { useEffect, useCallback, useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { getTurndownService } from '@/utils/turndownService';
import { marked } from 'marked';
import {
    Bold,
    Italic,
    Strikethrough,
    List,
    ListOrdered,
    Minus,
    Heading1,
    Heading2,
    Heading3,
    Link as LinkIcon,
    Image as ImageIcon,
    Code2,
    Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { processFile, uploadSingleFile } from '@/services/upload-service';

// Create lowlight instance with common languages
const lowlight = createLowlight(common);

// Configure marked for synchronous parsing
marked.setOptions({
    async: false,
});

// Helper to detect if content is HTML or Markdown
const isHtml = (content: string): boolean => {
    if (!content) return false;
    // Check if content starts with HTML tags
    const trimmed = content.trim();
    return trimmed.startsWith('<') && (
        trimmed.startsWith('<p>') ||
        trimmed.startsWith('<h') ||
        trimmed.startsWith('<ul>') ||
        trimmed.startsWith('<ol>') ||
        trimmed.startsWith('<div>') ||
        trimmed.startsWith('<br')
    );
};

// Convert Markdown to HTML (exported for use in other components)
export const markdownToHtml = (markdown: string): string => {
    if (!markdown) return '';
    if (isHtml(markdown)) return markdown;

    try {
        const html = marked.parse(markdown) as string;
        return html;
    } catch {
        // If parsing fails, wrap in paragraph
        return `<p>${markdown}</p>`;
    }
};

interface TiptapEditorProps {
    content: string;
    onChange?: (content: string) => void;
    onMarkdownChange?: (markdown: string) => void;
    onImageUpload?: (file: File) => Promise<string>;
    placeholder?: string;
    editable?: boolean;
    className?: string;
    minHeight?: string;
    showToolbar?: boolean;
    autoFocus?: boolean;
}

// Imperative handle exposed via ref so external code can insert content at
// the editor's current cursor position (chip-button-style variable insertion).
// Going through this avoids the markdown→html round-trip path that would
// otherwise re-setContent the whole editor and jump the cursor to the end.
export interface TiptapEditorHandle {
    insertContent: (text: string) => void;
}

// Toolbar button component
const ToolbarButton: React.FC<{
    editor: Editor;
    onClick: () => void;
    isActive: boolean;
    title: string;
    children: React.ReactNode;
}> = ({ editor: _editor, onClick, isActive, title, children }) => {
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        // Prevent default to avoid losing editor focus
        e.preventDefault();
    }, []);

    const handleClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
    }, [onClick]);

    return (
        <button
            type="button"
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            title={title}
            className={cn(
                'h-8 w-8 inline-flex items-center justify-center rounded text-muted-foreground transition-colors',
                'hover:bg-accent hover:text-foreground',
                isActive && 'bg-muted text-primary'
            )}
        >
            {children}
        </button>
    );
};

// Link input popover component
const LinkInput: React.FC<{
    editor: Editor;
    onClose: () => void;
    selectedText: string;
    selectionRange: { from: number; to: number };
}> = ({ editor, onClose, selectedText, selectionRange }) => {
    const { t } = useTranslation('common');
    // The input shows and edits the link text (which is also used as href)
    const [linkText, setLinkText] = useState(selectedText);
    const inputRef = useRef<HTMLInputElement>(null);

    const hasSelection = selectionRange.from !== selectionRange.to;

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const finalText = linkText.trim();

        if (!finalText) {
            // Remove link if text is empty
            if (hasSelection) {
                editor
                    .chain()
                    .focus()
                    .setTextSelection(selectionRange)
                    .unsetLink()
                    .run();
            }
            onClose();
            return;
        }

        if (hasSelection) {
            // Replace selected text with new link
            editor
                .chain()
                .focus()
                .setTextSelection(selectionRange)
                .deleteSelection()
                .insertContent(`<a href="${finalText}">${finalText}</a>`)
                .run();
        } else {
            // No selection: insert new link at cursor position
            editor
                .chain()
                .focus()
                .insertContent(`<a href="${finalText}">${finalText}</a>`)
                .run();
        }

        onClose();
    };

    const handleRemove = () => {
        if (hasSelection) {
            editor
                .chain()
                .focus()
                .setTextSelection(selectionRange)
                .unsetLink()
                .run();
        }
        onClose();
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2 p-3 bg-popover border border-border rounded-lg shadow-lg min-w-[280px]">
            <input
                ref={inputRef}
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder={t('ui.tiptapEditor.enterLink')}
                className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex items-center gap-2">
                <button
                    type="submit"
                    className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                    {t('action.save')}
                </button>
                {editor.isActive('link') && (
                    <button
                        type="button"
                        onClick={handleRemove}
                        className="px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        {t('action.remove')}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded hover:bg-accent"
                >
                    {t('action.cancel')}
                </button>
            </div>
        </form>
    );
};

// Toolbar component with force re-render on selection change
const Toolbar: React.FC<{
    editor: Editor | null;
    onImageUpload?: (file: File) => Promise<string>;
}> = ({ editor, onImageUpload }) => {
    const { t } = useTranslation('common');
    // Force re-render when editor state changes
    const [, setForceUpdate] = useState(0);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkSelection, setLinkSelection] = useState<{ text: string; from: number; to: number }>({ text: '', from: 0, to: 0 });
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!editor) return;

        const handleUpdate = () => {
            setForceUpdate((prev) => prev + 1);
        };

        editor.on('selectionUpdate', handleUpdate);
        editor.on('transaction', handleUpdate);

        return () => {
            editor.off('selectionUpdate', handleUpdate);
            editor.off('transaction', handleUpdate);
        };
    }, [editor]);

    const handleLinkClick = useCallback(() => {
        if (!editor) return;

        // Capture selection before opening popover
        const { from, to } = editor.state.selection;
        const text = from !== to ? editor.state.doc.textBetween(from, to) : '';
        setLinkSelection({ text, from, to });
        setShowLinkInput(true);
    }, [editor]);

    const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !editor) return;

        setIsUploading(true);

        try {
            let imageUrl: string;

            if (onImageUpload) {
                // Use custom upload function
                imageUrl = await onImageUpload(file);
            } else {
                // Default: use upload-service to upload and get real URL
                const processedFile = await processFile(file, {
                    enableCompression: true,
                    enableThumbnail: false,
                });

                const uploadResult = await uploadSingleFile(processedFile, {
                    sourceModule: 'editor',
                });

                if (!uploadResult.success || !uploadResult.publicUrl) {
                    throw new Error(uploadResult.error || 'Upload failed');
                }

                imageUrl = uploadResult.publicUrl;
            }

            // Insert image at cursor position using setImage command
            if (imageUrl) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor.chain().focus() as any).setImage({ src: imageUrl }).run();
            }
        } catch (error) {
            console.error('Image upload failed:', error);
        } finally {
            setIsUploading(false);
        }

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [editor, onImageUpload]);

    if (!editor) return null;

    // Helper to check if a format is active
    const isHeading1Active = editor.isActive('heading', { level: 1 });
    const isHeading2Active = editor.isActive('heading', { level: 2 });
    const isHeading3Active = editor.isActive('heading', { level: 3 });
    const isBoldActive = editor.isActive('bold');
    const isItalicActive = editor.isActive('italic');
    const isStrikeActive = editor.isActive('strike');
    const isCodeBlockActive = editor.isActive('codeBlock');
    const isBulletListActive = editor.isActive('bulletList');
    const isOrderedListActive = editor.isActive('orderedList');
    const isLinkActive = editor.isActive('link');

    return (
        <div className="relative">
            <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-border bg-muted/80">
                {/* Headings */}
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    isActive={isHeading1Active}
                    title={t('ui.tiptapEditor.heading1')}
                >
                    <Heading1 className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    isActive={isHeading2Active}
                    title={t('ui.tiptapEditor.heading2')}
                >
                    <Heading2 className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    isActive={isHeading3Active}
                    title={t('ui.tiptapEditor.heading3')}
                >
                    <Heading3 className="h-4 w-4" />
                </ToolbarButton>

                <div className="w-px h-6 bg-border mx-1" />

                {/* Text formatting */}
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={isBoldActive}
                    title={t('ui.tiptapEditor.bold')}
                >
                    <Bold className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={isItalicActive}
                    title={t('ui.tiptapEditor.italic')}
                >
                    <Italic className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    isActive={isStrikeActive}
                    title={t('ui.tiptapEditor.strikethrough')}
                >
                    <Strikethrough className="h-4 w-4" />
                </ToolbarButton>

                <div className="w-px h-6 bg-border mx-1" />

                {/* Link */}
                <ToolbarButton
                    editor={editor}
                    onClick={handleLinkClick}
                    isActive={isLinkActive}
                    title={t('ui.tiptapEditor.link')}
                >
                    <LinkIcon className="h-4 w-4" />
                </ToolbarButton>

                {/* Image Upload */}
                <ToolbarButton
                    editor={editor}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    isActive={false}
                    title={isUploading ? t('ui.tiptapEditor.uploading') : t('ui.tiptapEditor.insertImage')}
                >
                    {isUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <ImageIcon className="h-4 w-4" />
                    )}
                </ToolbarButton>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                />

                {/* Code Block */}
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    isActive={isCodeBlockActive}
                    title={t('ui.tiptapEditor.codeBlock')}
                >
                    <Code2 className="h-4 w-4" />
                </ToolbarButton>

                <div className="w-px h-6 bg-border mx-1" />

                {/* Lists */}
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={isBulletListActive}
                    title={t('ui.tiptapEditor.bulletList')}
                >
                    <List className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={isOrderedListActive}
                    title={t('ui.tiptapEditor.numberedList')}
                >
                    <ListOrdered className="h-4 w-4" />
                </ToolbarButton>

                <div className="w-px h-6 bg-border mx-1" />

                {/* Horizontal rule */}
                <ToolbarButton
                    editor={editor}
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    isActive={false}
                    title={t('ui.tiptapEditor.horizontalRule')}
                >
                    <Minus className="h-4 w-4" />
                </ToolbarButton>
            </div>

            {/* Link Input Popover */}
            {showLinkInput && (
                <div className="absolute top-full left-0 mt-1 z-10">
                    <LinkInput
                        editor={editor}
                        onClose={() => setShowLinkInput(false)}
                        selectedText={linkSelection.text}
                        selectionRange={{ from: linkSelection.from, to: linkSelection.to }}
                    />
                </div>
            )}
        </div>
    );
};

// HTML → Markdown 转换器已挪去 @/utils/turndownService：它与富文本编辑器无关，
// 留在这里会让「只需要一个转换器」的调用方（KB feed 的 EditableBody）连带把整个
// tiptap + lowlight 拖进自己的路由分包。这里 re-export，既有 import 路径不变。
export { getTurndownService };

export const TiptapEditor = forwardRef<TiptapEditorHandle, TiptapEditorProps>(({
    content,
    onChange,
    onMarkdownChange,
    onImageUpload,
    placeholder: _placeholder = 'Start typing...',
    editable = true,
    className,
    minHeight = '80px',
    showToolbar = true,
    autoFocus = false,
}, ref) => {
    // 只在首次挂载时把 content 转成 HTML 作为初始文档。
    // 不能依赖 content:后续同步走下面的 effect,这里每次输入都跑一遍 marked.parse 纯属浪费。
    const initialHtmlContentRef = useRef<string | null>(null);
    if (initialHtmlContentRef.current === null) {
        initialHtmlContentRef.current = markdownToHtml(content);
    }
    const initialHtmlContent = initialHtmlContentRef.current;
    const editorMinHeightStyle = useMemo(
        () => ({ '--tiptap-min-height': minHeight, minHeight } as React.CSSProperties),
        [minHeight]
    );

    // 编辑器最近一次向外 emit 的内容,用来识别父组件回传的"回声"更新。
    const lastEmittedRef = useRef<{ html: string; markdown: string | null } | null>(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                // Disable default code block since we use CodeBlockLowlight
                codeBlock: false,
                // Disable default link since we configure it separately below
                link: false,
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-primary underline hover:text-primary/80',
                },
            }),
            Image.configure({
                allowBase64: true,
                HTMLAttributes: {
                    class: 'max-w-full h-auto rounded-lg',
                },
            }),
            CodeBlockLowlight.configure({
                lowlight,
                HTMLAttributes: {
                    class: 'hljs',
                },
            }),
        ],
        content: initialHtmlContent,
        editable,
        autofocus: autoFocus,
        editorProps: {
            attributes: {
                class: cn(
                    'prose prose-sm max-w-none focus:outline-none px-3 py-2',
                    'prose-headings:font-semibold prose-headings:text-foreground prose-headings:mt-2 prose-headings:mb-1',
                    'prose-h1:text-xl prose-h2:text-lg prose-h3:text-base',
                    'prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:my-1',
                    'prose-ul:my-1 prose-ul:pl-5 prose-ol:my-1 prose-ol:pl-5',
                    'prose-li:my-0.5 prose-li:text-muted-foreground',
                    'prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none',
                    'prose-hr:my-3 prose-hr:border-border',
                    'prose-a:text-primary prose-a:underline hover:prose-a:text-primary',
                    'prose-img:rounded-lg prose-img:max-w-full'
                ),
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            let markdown: string | null = null;

            if (onMarkdownChange) {
                markdown = getTurndownService().turndown(html);
            }
            // 记下本次自己吐出去的值:父组件把它原样回传时不能再 setContent,
            // 否则整篇文档被重建、光标跳到末尾(markdown↔HTML 往返不是无损的,
            // 单纯比较 html 永远判定"不同")。
            lastEmittedRef.current = { html, markdown };

            onChange?.(html);
            if (markdown !== null) onMarkdownChange?.(markdown);
        },
    });

    // editable 变化必须显式下发:@tiptap/react v3 的 useEditor 在同步 options 时会
    // 强行保留 `editable: editor.isEditable`(见 dist/index.js 的 onRender),
    // 光靠 useEditor 的 editable 选项,prop 从 true 变 false 不会生效
    // (announcement published 后锁定、任务保存中禁编辑都会中招)。
    useEffect(() => {
        if (editor && editor.isEditable !== editable) editor.setEditable(editable);
    }, [editable, editor]);

    // Update content when prop changes (only if it's a genuine external change)
    useEffect(() => {
        if (!editor) return;

        // 回声:content 就是编辑器刚 emit 出去的内容 → 跳过,保住光标。
        const last = lastEmittedRef.current;
        if (last && (content === last.markdown || content === last.html)) return;

        const htmlContent = markdownToHtml(content);
        if (htmlContent === editor.getHTML()) return;

        // 真正的外部改动(加载草稿/切换语言等):重建文档,但尽量把光标留在原位。
        const { from, to } = editor.state.selection;
        editor.commands.setContent(htmlContent, { emitUpdate: false });
        const max = editor.state.doc.content.size;
        if (from <= max && to <= max) {
            editor.commands.setTextSelection({ from, to });
        }
    }, [content, editor]);

    // Expose imperative API for callers that need cursor-aware insertion
    // (e.g. chip-button variable insertion). Plain text only — chevron-style
    // placeholders like `{{xxx}}` are inserted as text nodes at the cursor.
    useImperativeHandle(ref, () => ({
        insertContent: (text: string) => {
            editor?.chain().focus().insertContent(text).run();
        },
    }), [editor]);

    return (
        <div
            className={cn(
                'border border-border rounded-lg overflow-hidden bg-background flex flex-col',
                !editable && 'bg-muted',
                className
            )}
            style={{ maxHeight: '400px' }}
        >
            {showToolbar && editable && (
                <div className="flex-shrink-0 sticky top-0 z-10 bg-background">
                    <Toolbar
                        editor={editor}
                        onImageUpload={onImageUpload}
                    />
                </div>
            )}
            <div className="flex-1 overflow-y-auto" style={editorMinHeightStyle}>
                <EditorContent
                    editor={editor}
                    style={editorMinHeightStyle}
                    className={cn(
                        "[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[var(--tiptap-min-height)]",
                        // Code block styles
                        "[&_.ProseMirror_pre]:bg-slate-900 [&_.ProseMirror_pre]:text-slate-100", /* 有意深底:代码块深色主题 */
                        "[&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:my-2",
                        "[&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_pre]:text-sm",
                        "[&_.ProseMirror_pre]:font-mono",
                        "[&_.ProseMirror_pre_code]:bg-transparent [&_.ProseMirror_pre_code]:p-0",
                        "[&_.ProseMirror_pre_code]:text-inherit [&_.ProseMirror_pre_code]:rounded-none",
                        // Syntax highlighting colors
                        "[&_.hljs-keyword]:text-teal-400",
                        "[&_.hljs-string]:text-green-400",
                        "[&_.hljs-number]:text-orange-400",
                        "[&_.hljs-function]:text-blue-400",
                        "[&_.hljs-comment]:text-muted-foreground",
                        "[&_.hljs-variable]:text-red-400",
                        "[&_.hljs-built_in]:text-cyan-400",
                        "[&_.hljs-attr]:text-yellow-400",
                        "[&_.hljs-title]:text-blue-400",
                        "[&_.hljs-params]:text-slate-300",
                        "[&_.hljs-literal]:text-orange-400",
                        "[&_.hljs-type]:text-cyan-400",
                    )}
                />
            </div>
        </div>
    );
});

TiptapEditor.displayName = 'TiptapEditor';

// Hook to get editor instance with methods
export const useTiptapEditor = (content: string, options?: Partial<TiptapEditorProps>) => {
    // Convert content to HTML if it's Markdown
    const initialHtmlContent = useMemo(() => markdownToHtml(content), [content]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
                codeBlock: false,
            }),
            Link.configure({
                openOnClick: false,
            }),
            Image,
            CodeBlockLowlight.configure({
                lowlight,
            }),
        ],
        content: initialHtmlContent,
        editable: options?.editable ?? true,
        autofocus: options?.autoFocus ?? false,
    });

    const getHTML = useCallback(() => {
        return editor?.getHTML() || '';
    }, [editor]);

    const getMarkdown = useCallback(() => {
        if (!editor) return '';
        return getTurndownService().turndown(editor.getHTML());
    }, [editor]);

    const setContent = useCallback((newContent: string) => {
        editor?.commands.setContent(newContent);
    }, [editor]);

    return {
        editor,
        getHTML,
        getMarkdown,
        setContent,
    };
};

export default TiptapEditor;
